import React from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';

import { formatInitialQueryParams } from '../../store/formatters';

let sentryInstance: typeof import('@sentry/react') | null = null;

export const getSentry = () => sentryInstance;

export const initSentry = async (dsn: string): Promise<void> => {
  if (!dsn) return;

  const { search } = window.location;
  const { brand, sessionId } = formatInitialQueryParams(search);

  const params = new URLSearchParams(search);
  const xChannel = params.get('xChannel') || '';
  const sentryTrace = params.get('sentryTrace') || '';
  const baggage = params.get('baggage') || '';

  const Sentry = await import(/* webpackChunkName: "sentry" */ '@sentry/react');

  sentryInstance = Sentry;

  const release = process.env.REACT_APP_RELEASE_TAG || process.env.REACT_APP_VERSION || 'unknown';

  const initSentryInstance = () => {
    Sentry.init({
      dsn,
      debug: false,
      release,
      environment: brand.toLowerCase(),
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 0.02,

      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
          detectRedirects: true,
        }),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
          useCompression: true,
        }),
      ],

      tracesSampleRate: 0.5,
      profileSessionSampleRate: 0.2,
      profileLifecycle: 'trace',
      maxBreadcrumbs: 20,
      beforeSendTransaction(transaction) {
        if (!transaction.spans) return transaction;
        return {
          ...transaction,
          spans: transaction.spans.filter((span) => {
            if (!span.timestamp || !span.start_timestamp) return true;
            return span.timestamp - span.start_timestamp > 0.005;
          }),
        };
      },
      ignoreErrors: [
        /chrome-extension:\/\//,
        /(ucConfig|xbrowser|swbrowser|runCustomize|processRandomSelector|onModuleDataArrival) is not defined/i,
        /Failed to execute 'getComputedStyle' on 'Window': parameter 1 is not of type 'Element'/,
      ],
      denyUrls: [/chrome-extension:\/\//],
      beforeSend(event) {
        // Filter out errors from browser extensions / injected scripts
        // These have frames with filename: '<anonymous>', 'undefined', or undefined
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
        const isFromExtension =
          frames?.length &&
          frames.every((frame) => {
            const filename = frame.filename;
            return !filename || filename === '<anonymous>' || filename === 'undefined';
          });
        if (isFromExtension) {
          return null;
        }
        return event;
      },
    });
  };

  if (sentryTrace && baggage) {
    Sentry.continueTrace(
      {
        sentryTrace,
        baggage,
      },
      initSentryInstance,
    );
  } else {
    initSentryInstance();
  }

  const { hostname, pathname } = window.location;

  Sentry.setTags({
    hostname,
    pathname,
    sessionId,
    brand,
    xChannel,
    release,
    env: process.env.REACT_APP_SENTRY_DSN?.includes('terrs.qwrt.xyz') ? 'stage' : 'prod',
  });
};
